import { RealtimeDBService } from '../firebase/unifiedDataService';
import {
  getStudentStats as getStudentStatsFS,
  setStudentStats,
  updateStudentStats,
  addQuizHistory,
  getQuizHistory as getQuizHistoryFS,
  addRecentActivity,
  getCourseStats as getCourseStatsFS,
  updateCourseStats
} from '../firebase/unifiedDataService';
import { Logger } from './logger';

const log = Logger.create('StudentStatsService');

/**
 * Student Statistics Service
 * Tracks and updates quiz participation statistics for registered students
 * 
 * Uses unifiedDataService for Firestore with Realtime DB fallback (auto-migration)
 */

/**
 * Try Firestore first, fallback to Realtime DB, then auto-migrate
 */
async function getStudentStatsWithFallback(studentId) {
  // Try Firestore first
  try {
    const stats = await getStudentStatsFS(studentId);
    if (stats) {
      return { stats, source: 'firestore' };
    }
  } catch (error) {
    log.warn('Failed to fetch from Firestore, trying Realtime DB:', error);
  }

  // Fallback to Realtime DB using RealtimeDBService
  try {
    const stats = await RealtimeDBService.get(`student_stats/${studentId}`);
    
    if (stats) {
      // Auto-migrate to Firestore in background
      setTimeout(async () => {
        try {
          await setStudentStats(studentId, stats);
          log.success(`Auto-migrated student stats for ${studentId}`);
        } catch (error) {
          log.error('Auto-migration failed', error);
        }
      }, 0);
      
      return { stats, source: 'realtime-db' };
    }
  } catch (error) {
    log.error('Failed to fetch from Realtime DB', error);
  }

  return { stats: null, source: null };
}

/**
 * Record a quiz participation for a student
 * @param {Object} params
 * @param {string} params.studentId - Student's user ID
 * @param {string} params.sessionId - Quiz session ID
 * @param {string} params.quizName - Name of the quiz
 * @param {string} params.courseId - Course ID (optional)
 * @param {string} params.courseName - Course name (optional)
 * @param {string} params.teacherUserId - Teacher's user ID (optional)
 * @param {number} params.totalQuestions - Total questions in quiz
 * @param {number} params.correctAnswers - Number of correct answers
 * @param {number} params.score - Score achieved (0-100)
 * @param {number} params.points - Points earned (gamification)
 * @returns {Promise<void>}
 */
export async function recordQuizParticipation({
  studentId,
  sessionId,
  quizName,
  courseId = null,
  courseName = null,
  teacherUserId = null,
  totalQuestions,
  correctAnswers,
  score,
  points = 0
}) {
  if (!studentId || !sessionId) {
    log.warn('recordQuizParticipation: Missing studentId or sessionId');
    return;
  }
  
  const timestamp = Date.now();
  
  try {
    // 1. Add to quiz history (Firestore sub-collection)
    await addQuizHistory(studentId, sessionId, {
      sessionId,
      quizName,
      courseId,
      courseName,
      teacherUserId,
      totalQuestions,
      correctAnswers,
      score,
      points,
      completedAt: timestamp
    });
    
    // 2. Update aggregate stats
    const { stats: currentStats } = await getStudentStatsWithFallback(studentId);
    const existingStats = currentStats || {};
    
    const newTotalQuizzes = (existingStats.totalQuizzesTaken || 0) + 1;
    const newTotalQuestions = (existingStats.totalQuestionsAnswered || 0) + totalQuestions;
    const newTotalCorrect = (existingStats.totalCorrectAnswers || 0) + correctAnswers;
    const newTotalPoints = (existingStats.totalPoints || 0) + points;
    
    // Calculate streak
    let newStreak = existingStats.currentStreak || 0;
    const lastQuizDate = existingStats.lastQuizDate ? new Date(existingStats.lastQuizDate) : null;
    const today = new Date();
    
    if (lastQuizDate) {
      const daysDiff = Math.floor((today - lastQuizDate) / (1000 * 60 * 60 * 24));
      if (daysDiff <= 1) {
        // Continue streak (same day or next day)
        if (daysDiff === 1) {
          newStreak++;
        }
        // If same day, keep current streak
      } else {
        // Break streak
        newStreak = 1;
      }
    } else {
      newStreak = 1;
    }
    
    // Prepare updated stats
    const updatedStats = {
      totalQuizzesTaken: newTotalQuizzes,
      totalQuestionsAnswered: newTotalQuestions,
      totalCorrectAnswers: newTotalCorrect,
      totalPoints: newTotalPoints,
      currentStreak: newStreak,
      bestStreak: Math.max(existingStats.bestStreak || 0, newStreak),
      lastQuizDate: timestamp,
      lastQuizId: sessionId,
      updatedAt: timestamp
    };
    
    await setStudentStats(studentId, updatedStats);
    
    // 3. Update course-specific stats if courseId provided
    if (courseId) {
      // Check if student is enrolled in the course, if not, auto-enroll
      if (teacherUserId) {
        await ensureStudentEnrolled({
          studentId,
          courseId,
          courseName,
          teacherUserId
        });
      }

      const currentCourseStats = await getCourseStatsFS(studentId, courseId) || {};
      
      const newCourseQuizzes = (currentCourseStats.quizzesTaken || 0) + 1;
      const newCourseQuestions = (currentCourseStats.questionsAnswered || 0) + totalQuestions;
      const newCourseCorrect = (currentCourseStats.correctAnswers || 0) + correctAnswers;
      
      // Calculate running average score
      const totalScoreSum = (currentCourseStats.averageScore || 0) * (currentCourseStats.quizzesTaken || 0) + score;
      const newAverageScore = totalScoreSum / newCourseQuizzes;
      
      await updateCourseStats(studentId, courseId, {
        courseId,
        courseName: courseName || currentCourseStats.courseName,
        quizzesTaken: newCourseQuizzes,
        questionsAnswered: newCourseQuestions,
        correctAnswers: newCourseCorrect,
        averageScore: Math.round(newAverageScore * 10) / 10,
        lastQuizDate: timestamp
      });
    }
    
    // 4. Update recent activity
    await addRecentActivity(studentId, {
      type: 'quiz_completed',
      sessionId,
      quizName,
      courseId,
      courseName,
      score,
      correctAnswers,
      totalQuestions,
      date: timestamp
    });
    
  } catch (error) {
    log.error('Error recording quiz participation (Firestore)', error);
    
    // Fallback to Realtime DB (for backward compatibility)
    await recordQuizParticipationRTDB({
      studentId,
      sessionId,
      quizName,
      courseId,
      courseName,
      teacherUserId,
      totalQuestions,
      correctAnswers,
      score,
      points
    });
  }
}

/**
 * Realtime DB fallback (legacy)
 */
async function recordQuizParticipationRTDB({
  studentId,
  sessionId,
  quizName,
  courseId,
  courseName,
  teacherUserId,
  totalQuestions,
  correctAnswers,
  score,
  points
}) {
  const timestamp = Date.now();
  
  // 1. Add to quiz history
  await RealtimeDBService.set(`student_stats/${studentId}/quizHistory/${sessionId}`, {
    sessionId,
    quizName,
    courseId,
    courseName,
    teacherUserId,
    totalQuestions,
    correctAnswers,
    score,
    points,
    completedAt: timestamp
  });
  
  // 2. Update aggregate stats
  const currentStats = await RealtimeDBService.get(`student_stats/${studentId}`) || {};
  
  const newTotalQuizzes = (currentStats.totalQuizzesTaken || 0) + 1;
  const newTotalQuestions = (currentStats.totalQuestionsAnswered || 0) + totalQuestions;
  const newTotalCorrect = (currentStats.totalCorrectAnswers || 0) + correctAnswers;
  const newTotalPoints = (currentStats.totalPoints || 0) + points;
  
  // Calculate streak
  let newStreak = currentStats.currentStreak || 0;
  const lastQuizDate = currentStats.lastQuizDate ? new Date(currentStats.lastQuizDate) : null;
  const today = new Date();
  
  if (lastQuizDate) {
    const daysDiff = Math.floor((today - lastQuizDate) / (1000 * 60 * 60 * 24));
    if (daysDiff <= 1) {
      // Continue streak (same day or next day)
      if (daysDiff === 1) {
        newStreak++;
      }
      // If same day, keep current streak
    } else {
      // Break streak
      newStreak = 1;
    }
  } else {
    newStreak = 1;
  }
  
  await RealtimeDBService.update(`student_stats/${studentId}`, {
    totalQuizzesTaken: newTotalQuizzes,
    totalQuestionsAnswered: newTotalQuestions,
    totalCorrectAnswers: newTotalCorrect,
    totalPoints: newTotalPoints,
    currentStreak: newStreak,
    bestStreak: Math.max(currentStats.bestStreak || 0, newStreak),
    lastQuizDate: timestamp,
    lastQuizId: sessionId,
    updatedAt: timestamp
  });
  
  // 3. Update course-specific stats if courseId provided
  if (courseId) {
    if (teacherUserId) {
      await ensureStudentEnrolled({
        studentId,
        courseId,
        courseName,
        teacherUserId
      });
    }

    const currentCourseStats = await RealtimeDBService.get(`student_stats/${studentId}/courseStats/${courseId}`) || {};
    
    const newCourseQuizzes = (currentCourseStats.quizzesTaken || 0) + 1;
    const newCourseQuestions = (currentCourseStats.questionsAnswered || 0) + totalQuestions;
    const newCourseCorrect = (currentCourseStats.correctAnswers || 0) + correctAnswers;
    
    // Calculate running average score
    const totalScoreSum = (currentCourseStats.averageScore || 0) * (currentCourseStats.quizzesTaken || 0) + score;
    const newAverageScore = totalScoreSum / newCourseQuizzes;
    
    await RealtimeDBService.update(`student_stats/${studentId}/courseStats/${courseId}`, {
      courseId,
      courseName: courseName || currentCourseStats.courseName,
      quizzesTaken: newCourseQuizzes,
      questionsAnswered: newCourseQuestions,
      correctAnswers: newCourseCorrect,
      averageScore: Math.round(newAverageScore * 10) / 10,
      lastQuizDate: timestamp,
      updatedAt: timestamp
    });
  }
  
  // 4. Update recent activity
  let activities = await RealtimeDBService.get(`student_stats/${studentId}/recentActivity`) || [];
  
  // Ensure activities is an array
  if (!Array.isArray(activities)) {
    activities = Object.values(activities || {});
  }
  
  // Add new activity at the beginning
  activities.unshift({
    type: 'quiz_completed',
    sessionId,
    quizName,
    courseId,
    courseName,
    score,
    correctAnswers,
    totalQuestions,
    date: timestamp
  });
  
  // Keep only last 20 activities
  activities = activities.slice(0, 20);
  
  await RealtimeDBService.set(`student_stats/${studentId}/recentActivity`, activities);
}

/**
 * Get student stats
 * @param {string} studentId - Student's user ID
 * @returns {Promise<Object>} - Student statistics
 */
export async function getStudentStats(studentId) {
  const { stats } = await getStudentStatsWithFallback(studentId);
  
  if (!stats) {
    return {
      totalQuizzesTaken: 0,
      totalQuestionsAnswered: 0,
      totalCorrectAnswers: 0,
      totalPoints: 0,
      currentStreak: 0,
      bestStreak: 0,
      courseStats: {},
      recentActivity: []
    };
  }
  
  return stats;
}

/**
 * Get recent activity for a student
 * @param {string} studentId - Student's user ID
 * @param {number} limit - Max number of activities to return
 * @returns {Promise<Array>} - Recent activities
 */
export async function getRecentActivity(studentId, limit = 10) {
  const { stats } = await getStudentStatsWithFallback(studentId);
  
  if (!stats || !stats.recentActivity) {
    return [];
  }
  
  const activities = Array.isArray(stats.recentActivity) 
    ? stats.recentActivity 
    : Object.values(stats.recentActivity || {});
  
  return activities.slice(0, limit);
}

/**
 * Get quiz history for a student
 * @param {string} studentId - Student's user ID
 * @param {string} courseId - Filter by course (optional)
 * @returns {Promise<Array>} - Quiz history entries
 */
export async function getQuizHistory(studentId, courseId = null) {
  try {
    // Try Firestore first (sub-collection)
    const history = await getQuizHistoryFS(studentId, courseId);
    if (history && history.length > 0) {
      return history;
    }
  } catch (error) {
    log.warn('Failed to fetch quiz history from Firestore:', error);
  }

  // Fallback to Realtime DB using RealtimeDBService
  try {
    const historyData = await RealtimeDBService.get(`student_stats/${studentId}/quizHistory`);
    
    if (!historyData) {
      return [];
    }
    
    let history = Object.values(historyData);
    
    if (courseId) {
      history = history.filter(h => h.courseId === courseId);
    }
    
    // Sort by completion date, newest first
    history = history.sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));
    
    // Auto-migrate to Firestore in background
    if (history.length > 0) {
      setTimeout(async () => {
        try {
          for (const entry of history) {
            await addQuizHistory(studentId, entry.sessionId, entry);
          }
          log.success(`Auto-migrated ${history.length} quiz history entries for ${studentId}`);
        } catch (error) {
          log.error('Auto-migration of quiz history failed', error);
        }
      }, 0);
    }
    
    return history;
  } catch (error) {
    log.error('Failed to fetch quiz history from Realtime DB', error);
    return [];
  }
}

/**
 * Ensure student is enrolled in course (auto-enrollment)
 * @param {Object} params
 * @param {string} params.studentId - Student user ID
 * @param {string} params.courseId - Course ID
 * @param {string} params.courseName - Course name
 * @param {string} params.teacherUserId - Teacher user ID
 * @returns {Promise<void>}
 */
async function ensureStudentEnrolled({
  studentId,
  courseId,
  courseName,
  teacherUserId
}) {
  const enrollmentKey = `${teacherUserId}_${courseId}`;
  
  // Check if already enrolled using RealtimeDBService
  const existingEnrollment = await RealtimeDBService.get(
    `students/${studentId}/enrolledCourses/${enrollmentKey}`
  );
  
  if (existingEnrollment && existingEnrollment.status === 'active') {
    // Already enrolled
    return;
  }

  const now = Date.now();

  // Add to student's enrolled courses
  await RealtimeDBService.set(`students/${studentId}/enrolledCourses/${enrollmentKey}`, {
    courseId,
    teacherUserId,
    courseName,
    enrolledAt: now,
    status: 'active',
    enrolledVia: 'quiz_registration'
  });

  // Get student profile for name and email
  const studentData = await RealtimeDBService.get(`students/${studentId}`) || {};

  // Add to course enrollments (teacher view)
  await RealtimeDBService.set(
    `course_enrollments/${teacherUserId}/${courseId}/${studentId}`,
    {
      studentId,
      studentEmail: studentData.email || '',
      studentName: studentData.displayName || 'Unbekannt',
      enrolledAt: now,
      enrolledVia: 'quiz_registration',
      inviteCode: null,
      status: 'active'
    }
  );

  log.success(`Auto-enrolled student ${studentId} in course ${courseId}`);
}

/**
 * Enroll student in course when joining quiz
 * Called when a logged-in student joins a quiz
 * @param {Object} params
 * @param {string} params.studentId - Student's user ID
 * @param {string} params.teacherUserId - Teacher's user ID
 * @param {string} params.courseId - Course ID
 * @param {string} params.courseName - Course name
 * @returns {Promise<void>}
 */
export async function enrollStudentOnQuizJoin({
  studentId,
  teacherUserId,
  courseId,
  courseName
}) {
  if (!studentId || !teacherUserId || !courseId) {
    log.warn('enrollStudentOnQuizJoin: Missing required parameters');
    return;
  }
  
  try {
    await ensureStudentEnrolled({
      studentId,
      courseId,
      courseName,
      teacherUserId
    });
    log.success(`Student ${studentId} enrolled in course ${courseId} via quiz join`);
  } catch (error) {
    log.error('Error enrolling student on quiz join', error);
    // Don't throw - enrollment is not critical for quiz participation
  }
}

/**
 * Remove activities for a specific session from all students
 * @param {string} sessionId - Session ID to remove activities for
 * @returns {Promise<void>}
 */
export async function removeActivitiesForSession(sessionId) {
  try {
    // Get all students from Realtime DB
    const allStats = await RealtimeDBService.get('student_stats') || {};
    
    // Process each student
    for (const [studentId, stats] of Object.entries(allStats)) {
      if (!stats || !stats.recentActivity) continue;
      
      let activities = Array.isArray(stats.recentActivity) 
        ? stats.recentActivity 
        : Object.values(stats.recentActivity || {});
      
      // Filter out activities for this session
      const filteredActivities = activities.filter(activity => activity.sessionId !== sessionId);
      
      // Update only if there were changes
      if (filteredActivities.length !== activities.length) {
        await RealtimeDBService.set(`student_stats/${studentId}/recentActivity`, filteredActivities);
        log.info(`Removed ${activities.length - filteredActivities.length} activities for session ${sessionId} from student ${studentId}`);
      }
    }
    
    log.info(`Successfully removed activities for session ${sessionId} from all students`);
  } catch (error) {
    log.error('Error removing activities for session:', error);
    throw error;
  }
}

export default {
  recordQuizParticipation,
  getStudentStats,
  getRecentActivity,
  getQuizHistory,
  enrollStudentOnQuizJoin,
  removeActivitiesForSession
};
